// =============================
// File: src/components/admin/DocEditor.jsx
// =============================
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Eye, Send, Sparkles, ShieldCheck, WandSparkles } from 'lucide-react';
import axios from 'axios';
import DocContentBuilder from './DocContentBuilder';

const API_URL = process.env.REACT_APP_API_URL;

function DocEditor({ doc, categories, onBack, onSave }) {
  const [availableCategories, setAvailableCategories] = useState(categories || []);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    short_description: '',
    category_id: '',
    role_access: 'customer',
    is_featured: false,
    featured_order: null,
    tags: [],
    customer_intent: '',
    next_review_at: '',
    ai_generated: false
  });
  const [contentBlocks, setContentBlocks] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const [changeSummary, setChangeSummary] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAcceptingCategory, setIsAcceptingCategory] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    setAvailableCategories(categories || []);
  }, [categories]);

  useEffect(() => {
    if (doc) {
      setFormData({
        title: doc.title,
        slug: doc.slug,
        short_description: doc.short_description,
        category_id: doc.category_id,
        role_access: doc.role_access,
        is_featured: doc.is_featured,
        featured_order: doc.featured_order,
        tags: doc.tags || [],
        customer_intent: doc.customer_intent || '',
        next_review_at: doc.next_review_at ? doc.next_review_at.slice(0, 10) : '',
        ai_generated: Boolean(doc.ai_generated)
      });
      setContentBlocks(doc.content_blocks || []);
      setDocId(doc.id);
    }
  }, [doc]);

  useEffect(() => {
    if (!docId) return;
    axios.get(`${API_URL}/api/admin/docs/${docId}/versions`)
      .then(response => setVersions(response.data.versions || []))
      .catch(error => console.error('Error loading versions:', error));
  }, [docId]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!doc && formData.title) {
      const slug = formData.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, doc]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }

  async function handleSave({ publish = false, submitForReview = false } = {}) {
    // Validation
    if (!formData.title.trim()) {
      alert('El título es obligatorio');
      return;
    }
    if (!formData.slug.trim()) {
      alert('El slug es obligatorio');
      return;
    }
    if (!formData.short_description.trim()) {
      alert('La descripción corta es obligatoria');
      return;
    }
    if (!formData.category_id) {
      alert('Debes seleccionar una categoría');
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        metadata: {
          ...formData,
          category_id: Number(formData.category_id),
          next_review_at: formData.next_review_at || null,
        },
        content_blocks: contentBlocks,
        change_summary: changeSummary || (formData.ai_generated ? 'Borrador generado y revisado con asistencia de IA' : 'Actualización del documento'),
        submit_for_review: submitForReview,
      };
      const saveResponse = docId
        ? await axios.put(`${API_URL}/api/admin/docs/${docId}/content`, payload)
        : await axios.post(`${API_URL}/api/admin/docs/content`, payload);
      const currentDocId = saveResponse.data.doc.id;
      setDocId(currentDocId);
      if (publish) {
        await axios.post(`${API_URL}/api/admin/docs/${currentDocId}/publish`);
      }

      alert(publish ? 'Documento publicado exitosamente' : submitForReview ? 'Documento enviado a revisión' : 'Borrador versionado guardado exitosamente');
      onSave();
    } catch (error) {
      console.error('Error saving doc:', error);
      alert('Error al guardar el documento');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAIGenerate() {
    try {
      setIsGenerating(true);
      setAiResult(null);
      const response = await axios.post(`${API_URL}/api/admin/docs/ai/generate`, {
        prompt: aiPrompt.trim(),
        role_access: formData.role_access || 'customer',
      });
      const { draft } = response.data;
      const suggestedCategory = draft.category_recommendation?.category;
      setFormData(previous => ({
        ...previous,
        title: draft.title,
        slug: draft.slug,
        short_description: draft.short_description,
        role_access: draft.role_access || previous.role_access,
        category_id: suggestedCategory?.id || previous.category_id || '',
        tags: draft.tags || [],
        customer_intent: draft.customer_intent || '',
        ai_generated: true,
      }));
      setContentBlocks((draft.content_blocks || []).map((block, index) => ({ ...block, id: block.id || `ai-${Date.now()}-${index}`, order_index: index })));
      setChangeSummary(`Borrador IA: ${response.data.selected_topic}`);
      setAiResult(response.data);
    } catch (error) {
      console.error('AI document generation failed:', error);
      alert(error.response?.data?.message || error.response?.data?.error || 'No se pudo generar el documento con IA');
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleAcceptAICategory() {
    if (!aiResult?.request_id) return;
    try {
      setIsAcceptingCategory(true);
      const response = await axios.post(`${API_URL}/api/admin/docs/ai/categories/accept`, {
        request_id: aiResult.request_id,
      });
      const acceptedCategory = response.data.category;
      setAvailableCategories(previous => {
        const withoutDuplicate = previous.filter(category => category.id !== acceptedCategory.id);
        return [...withoutDuplicate, acceptedCategory].sort((a, b) =>
          (a.order_index || 0) - (b.order_index || 0) || a.name.localeCompare(b.name)
        );
      });
      setFormData(previous => ({ ...previous, category_id: acceptedCategory.id }));
      setAiResult(previous => ({
        ...previous,
        draft: {
          ...previous.draft,
          accepted_category_id: acceptedCategory.id,
          category_recommendation: {
            ...previous.draft.category_recommendation,
            status: 'existing',
            category: acceptedCategory,
            requires_confirmation: false,
          },
        },
      }));
    } catch (error) {
      console.error('AI category acceptance failed:', error);
      alert(error.response?.data?.error || 'No se pudo crear o seleccionar la categoría recomendada');
    } finally {
      setIsAcceptingCategory(false);
    }
  }

  async function handleRestoreVersion(version) {
    if (!window.confirm(`¿Restaurar la versión ${version.version_number} como un nuevo borrador?`)) return;
    try {
      const response = await axios.post(`${API_URL}/api/admin/docs/${docId}/versions/${version.id}/restore`);
      const restored = response.data.doc;
      setFormData(previous => ({
        ...previous,
        title: restored.title,
        slug: restored.slug,
        short_description: restored.short_description,
        category_id: restored.category_id,
        role_access: restored.role_access,
        is_featured: restored.is_featured,
        featured_order: restored.featured_order,
        tags: restored.tags || [],
        customer_intent: restored.customer_intent || '',
        next_review_at: restored.next_review_at ? restored.next_review_at.slice(0, 10) : '',
        ai_generated: Boolean(restored.ai_generated),
      }));
      setContentBlocks(restored.content_blocks || []);
      setChangeSummary(`Restauración de la versión ${version.version_number}`);
      const versionsResponse = await axios.get(`${API_URL}/api/admin/docs/${docId}/versions`);
      setVersions(versionsResponse.data.versions || []);
    } catch (error) {
      alert(error.response?.data?.error || 'No se pudo restaurar la versión');
    }
  }

  function handlePreview() {
    if (docId) {
      window.open(`/#/user-docs/${formData.slug}`, '_blank');
    } else {
      alert('Primero guarda el documento para ver la vista previa');
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-pink-600 shadow-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">
                {doc ? 'Editar Documento' : 'Nuevo Documento'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handlePreview}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Vista Previa</span>
              </button>
              <button
                onClick={() => handleSave({ publish: false })}
                disabled={isSaving}
                className="px-4 py-2 bg-white text-brand-primary hover:bg-white/90 rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{isSaving ? 'Guardando...' : 'Guardar'}</span>
              </button>
              <button
                onClick={() => handleSave({ submitForReview: true })}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden xl:inline">Enviar a revisión</span>
              </button>
              <button
                onClick={() => handleSave({ publish: true })}
                disabled={isSaving}
                className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Guardar y publicar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Metadata Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-24">
              <h2 className="text-lg font-bold text-slate-900 font-montserrat mb-4">
                Información del Documento
              </h2>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Ej: Cómo hacer login"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Slug (URL) *
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="como-hacer-login"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm"
                  />
                  <p className="text-xs text-slate-500 font-montserrat mt-1">
                    URL: /user-docs/{formData.slug || '...'}
                  </p>
                </div>

                {/* Short Description */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Descripción Corta *
                  </label>
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Breve descripción para las tarjetas..."
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm resize-none"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Categoría *
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                  >
                    <option value="">Seleccionar categoría</option>
                    {availableCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role Access */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Acceso por Rol *
                  </label>
                  <select
                    name="role_access"
                    value={formData.role_access}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                  >
                    <option value="all">Todos los roles</option>
                    <option value="customer">Solo Usuarios</option>
                    <option value="driver">Solo Conductores</option>
                    <option value="provider">Solo Proveedores</option>
                    <option value="admin">Solo Administradores</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Intención del usuario
                  </label>
                  <input
                    type="text"
                    name="customer_intent"
                    value={formData.customer_intent}
                    onChange={handleChange}
                    placeholder="Ej: agregar productos al inventario"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Etiquetas
                  </label>
                  <input
                    type="text"
                    value={(formData.tags || []).join(', ')}
                    onChange={(event) => setFormData(previous => ({
                      ...previous,
                      tags: event.target.value.split(',').map(item => item.trim()).filter(Boolean)
                    }))}
                    placeholder="inventario, productos, sku"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Próxima revisión
                  </label>
                  <input
                    type="date"
                    name="next_review_at"
                    value={formData.next_review_at}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Resumen del cambio
                  </label>
                  <textarea
                    value={changeSummary}
                    onChange={(event) => setChangeSummary(event.target.value)}
                    rows={2}
                    placeholder="Qué cambió y por qué..."
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm resize-none"
                  />
                </div>

                {/* Featured Checkbox */}
                <div className="pt-4 border-t border-slate-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                      className="w-5 h-5 text-brand-primary rounded focus:ring-2 focus:ring-brand-primary"
                    />
                    <span className="text-sm font-semibold text-slate-700 font-montserrat">
                      Destacar en banner
                    </span>
                  </label>
                  <p className="text-xs text-slate-500 font-montserrat mt-2 ml-8">
                    Los documentos destacados aparecen en el carrusel de la página principal (máx. 5)
                  </p>
                </div>

                {versions.length > 0 && (
                  <div className="pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-bold text-slate-800 mb-2">Historial de versiones</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {versions.slice(0, 8).map(version => (
                        <div key={version.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 p-2">
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-700">v{version.version_number} · {version.status}</p>
                            <p className="truncate text-[10px] text-slate-500">{version.change_summary || 'Sin resumen'}</p>
                          </div>
                          <button type="button" onClick={() => handleRestoreVersion(version)} className="text-[10px] font-bold text-brand-primary hover:underline">Restaurar</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content Builder */}
          <div className="lg:col-span-2">
            <div className="mb-6 overflow-hidden rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-amber-50 shadow-sm">
              <div className="flex items-start gap-3 border-b border-purple-100 p-5">
                <div className="rounded-xl bg-[#B1336E] p-3 text-white"><WandSparkles className="h-5 w-5" /></div>
                <div>
                  <h2 className="font-extrabold text-slate-950">Autor IA de Fast Max</h2>
                  <p className="text-xs font-semibold text-slate-600">Escribe pocas palabras o deja vacío para que la IA elija una oportunidad basada en búsquedas sin respuesta y conocimiento aprobado.</p>
                </div>
              </div>
              <div className="space-y-3 p-5">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-600">
                    Audiencia del documento y contexto para la IA
                  </span>
                  <select
                    value={formData.role_access}
                    onChange={(event) => setFormData(previous => ({
                      ...previous,
                      role_access: event.target.value,
                    }))}
                    className="w-full rounded-xl border border-purple-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
                  >
                    <option value="customer">Usuario / tienda</option>
                    <option value="driver">Conductor</option>
                    <option value="provider">Proveedor</option>
                    <option value="admin">Administrador</option>
                    <option value="all">Todos los roles</option>
                  </select>
                  <span className="mt-1 block text-[11px] font-semibold text-slate-500">
                    La IA solo recibirá fuentes aprobadas compatibles con esta audiencia.
                  </span>
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(event) => setAiPrompt(event.target.value)}
                  rows={3}
                  placeholder="Ej: cómo agregar productos al inventario (opcional)"
                  className="w-full resize-none rounded-xl border border-purple-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
                />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-600" />Solo usa fuentes aprobadas; nunca publica automáticamente.</div>
                  <button
                    type="button"
                    onClick={handleAIGenerate}
                    disabled={isGenerating}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#B1336E] px-4 py-2.5 text-sm font-black text-white hover:bg-[#98295f] disabled:opacity-50"
                  >
                    <Sparkles className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
                    {isGenerating ? 'Creando estructura...' : aiPrompt.trim() ? 'Crear documento con IA' : 'IA elige y crea'}
                  </button>
                </div>
                {aiResult && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                    <p className="font-black">Borrador completo generado: {aiResult.selected_topic}</p>
                    <p className="mt-1 font-semibold">Modelo: {aiResult.model} · Fuentes: {(aiResult.knowledge_sources || []).map(source => source.title).join(', ')}</p>
                    {aiResult.draft?.category_recommendation && (
                      <div className={`mt-3 rounded-lg border p-3 ${
                        aiResult.draft.category_recommendation.status === 'existing'
                          ? 'border-emerald-300 bg-white'
                          : 'border-amber-300 bg-amber-50'
                      }`}>
                        <p className="font-black text-slate-900">
                          {aiResult.draft.category_recommendation.status === 'existing'
                            ? `Categoría seleccionada: ${aiResult.draft.category_recommendation.category?.name}`
                            : `Nueva categoría propuesta: ${aiResult.draft.category_recommendation.proposed_name}`}
                        </p>
                        <p className="mt-1 font-semibold text-slate-600">
                          {aiResult.draft.category_recommendation.reason}
                        </p>
                        {aiResult.draft.category_recommendation.status === 'proposed_new' && (
                          <button
                            type="button"
                            onClick={handleAcceptAICategory}
                            disabled={isAcceptingCategory}
                            className="mt-3 rounded-lg bg-amber-600 px-3 py-2 font-black text-white hover:bg-amber-700 disabled:opacity-50"
                          >
                            {isAcceptingCategory ? 'Creando categoría...' : 'Crear y usar esta categoría'}
                          </button>
                        )}
                      </div>
                    )}
                    {(aiResult.draft?.review_flags || []).length > 0 && (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-800">
                        {aiResult.draft.review_flags.map((flag, index) => <li key={index}>{flag}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
            <DocContentBuilder
              blocks={contentBlocks}
              onBlocksChange={setContentBlocks}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocEditor;
